import React, { useState, useEffect } from 'react';
import type { AuthUser } from './types';
import { Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import AppShell from './components/layout/AppShell';
import DashboardRoute from './components/layout/DashboardRoute';
import ModuleRoute from './components/layout/ModuleRoute';
import GoogleTranslateShim, { setTranslateCookie, clearTranslateCookie } from './components/layout/GoogleTranslateShim';
import ChurchConfigSync from './components/layout/ChurchConfigSync';
import PublicRegistration from './features/registration/PublicRegistration';
import ApprovalsInbox from './features/approvals/ApprovalsInbox';
import { AlwaysLight } from './theme/ThemeContext';
import { getStoredUser, storeSession, clearSession, isGlobalReadOnly } from './utils/auth';
import { SETTINGS_ROUTE } from './routes';

const App = () => {
  // The stored session is read before the first render. Reading it in an
  // effect left `user` null for one render, during which a module URL's
  // permission check failed and redirected to the dashboard — so a refresh
  // (or a bookmark) on any module page bounced home.
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser());

  // Global read-only lock — CSS in index.css disables inputs when this is set.
  useEffect(() => {
    const locked = Boolean(user) && isGlobalReadOnly(user);
    document.body.classList.toggle('read-only-mode', locked);
  }, [user]);

  const handleLogin = (userData: AuthUser, langCode: string, jwtToken: string) => {
    storeSession(userData, langCode, jwtToken);
    setTranslateCookie(langCode);
    setUser(userData);
    // Full reload so Google Translate re-runs against the new language cookie.
    window.location.reload();
  };

  const handleLogout = () => {
    clearSession();
    clearTranslateCookie();
    window.location.reload();
  };


  return (
    <>
      <GoogleTranslateShim />
      <ChurchConfigSync />

      {!user ? (
        // Any URL while signed out lands on the login screen — except the
        // public family registration form, which needs no account.
        <Routes>
          <Route path="register/:token" element={<AlwaysLight><PublicRegistration /></AlwaysLight>} />
          <Route path="*" element={<Login onLogin={handleLogin} />} />
        </Routes>
      ) : (
        <Routes>
          {/* Outside AppShell, so staff opening the shared link see exactly
              what a family sees. */}
          <Route path="register/:token" element={<AlwaysLight><PublicRegistration /></AlwaysLight>} />
          <Route element={<AppShell user={user} onLogout={handleLogout} />}>
            <Route index element={<DashboardRoute user={user} />} />
            {/* Settings carries its left-menu section in the URL, so it is
                matched before the generic one-segment module route. */}
            <Route
              path={`${SETTINGS_ROUTE}/:section?`}
              element={<ModuleRoute user={user} moduleRoute={SETTINGS_ROUTE} />}
            />
            {/* The approvals inbox — every signed-in user; the server decides what each sees. */}
            <Route path="approvals" element={<ApprovalsInbox />} />
            <Route path=":moduleRoute" element={<ModuleRoute user={user} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      )}
    </>
  );
};

export default App;
